import { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from "react"
import { animate, motion, stagger, useAnimate, useMotionValue } from "framer-motion";
import { createUseGesture, pinchAction, dragAction, wheelAction } from "@use-gesture/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import {  useGameSounds, useMediaQuery } from "@educaplay/core";
import { CenterIcon, ZoomInIcon, ZoomOutIcon } from "../Icons";
import { CRUCIGRAMA_SOUNDS } from "../../../utils/constants";
import classes from "./InteractiveBox.module.scss"
import { useSelector } from "react-redux";

const useGesture = createUseGesture([pinchAction, dragAction, wheelAction]);
const getCursor = (tagName) => {
    switch (tagName) {
        case "BUTTON":
            return "pointer";
        case "INPUT":
            return "text";
        default:
            return "grab";
    }
}

const maxScale = 5;
const minScale = 1;
const RatioWheel = 400;

export const InteractiveBox = forwardRef(function InteractiveBox(props, ref) {
    const { children, currentScreen } = props;
    const [cursor, setCursor] = useState("grab");

    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const scale = useMotionValue(1);
    const [zooming, setZooming] = useState(false);
    const [bounds, setBounds] = useState({
        width: "fit-content",
        height: "fit-content",
        x: 0,
        y: 0
    });

    const interactiveRef = useRef(null);
    const isTouchDevice = useMediaQuery("(hover:none)") || ('maxTouchPoints' in navigator && navigator.maxTouchPoints > 0);
    const gameSound = useGameSounds();

    const [containerRef, anima] = useAnimate();
    const boxRef = useRef(null)
    const initialBoundsRef = useRef(null)

    const [childrenScale, setChildrenScale] = useState(1)
    const isFullScreen = useSelector(state => state.settings.isFullscreen);

    useImperativeHandle(ref, () => ({
        animateEntry: async () => {
            const sequence = [
                ["[data-animate-id='nav-button']", { opacity: 1, y: 0, x: 0 }, { delay: stagger(0.15) }]
            ]
            return anima(sequence);
        },
    }), [anima]);


    const getBounds = useCallback(() => {
        const imageBounds = interactiveRef.current.getBoundingClientRect();
        const containerBounds = containerRef.current.getBoundingClientRect();

        const heightOverflow = Math.min(imageBounds.height, containerBounds.height) / 2;
        const widthOverflow = Math.min(imageBounds.width, containerBounds.width) / 2;

        const widthOverhang = (imageBounds.width - bounds.width) / 2;
        const heightOverhang = (imageBounds.height - bounds.height) / 2;

        const isWidthOutContainer = imageBounds.width >= containerBounds.width;
        const isHeightOutContainer = imageBounds.height >= containerBounds.height;

        return {
            right: isWidthOutContainer
                ? widthOverhang + widthOverflow
                : containerBounds.width - imageBounds.width + widthOverhang + widthOverflow,
            left: isWidthOutContainer
                ? -(imageBounds.width - containerBounds.width) + widthOverhang - widthOverflow
                : widthOverhang - widthOverflow,
            bottom: isHeightOutContainer
                ? heightOverhang + heightOverflow
                : containerBounds.height - imageBounds.height + heightOverhang + heightOverflow,
            top: isHeightOutContainer
                ? -(imageBounds.height - containerBounds.height) + heightOverhang - heightOverflow
                : heightOverhang - heightOverflow,
        };
    }, [bounds, containerRef]);

    const calculateAdjustedBounds = useCallback((point) => {
        const bounds = getBounds();

        if (point.x < bounds.left) {
            point.x = bounds.left;
        } else if (point.x > bounds.right) {
            point.x = bounds.right;
        }

        if (point.y < bounds.top) {
            point.y = bounds.top;
        } else if (point.y > bounds.bottom) {
            point.y = bounds.bottom;
        }

        return point;
    }, [getBounds]);

    useEffect(() => {
        const handleMouseLeave = () => {
            setCursor("grab");
        }

        const handleFocus = async (e) => {
            if (e.target.tagName === "INPUT") {
                const animateWordId = e.target.getAttribute("data-animate-wordid");
                const cells = document.querySelectorAll(`[data-animate-wordid="${animateWordId}"]`);

                let minLeft = Number.MAX_SAFE_INTEGER;
                let maxRight = Number.MIN_SAFE_INTEGER;

                cells.forEach(cell => {
                    const bounds = cell.getBoundingClientRect();
                    if (bounds.left < minLeft) {
                        minLeft = bounds.left;
                    }

                    if (bounds.right > maxRight) {
                        maxRight = bounds.right;
                    }
                });

                const width = maxRight - minLeft;

                const elementBounds = e.target.getBoundingClientRect();
                const containerBounds = containerRef.current.getBoundingClientRect();
                const centerOffsetY = document.documentElement.clientHeight * -.15;

                const hasWordSpace = width < containerBounds.width;

                const newX = hasWordSpace
                    ? containerBounds.width / 2 - width / 2 - minLeft
                    : containerBounds.width / 2 - elementBounds.width / 2 - elementBounds.left;

                const newY = containerBounds.height / 2 - elementBounds.height / 2 - elementBounds.top + centerOffsetY;

                const bankHeight = document.documentElement.clientHeight * 0.12;

                const outOfBounds = (
                    elementBounds.x + elementBounds.width > containerBounds.width + containerBounds.x ||
                    elementBounds.y + elementBounds.height > containerBounds.height + containerBounds.y ||
                    elementBounds.x < containerBounds.x ||
                    elementBounds.y < containerBounds.y + bankHeight
                )

                if (outOfBounds || isTouchDevice) {
                    const promises = [
                        animate(x, x.get() + newX, {
                            type: "tween",
                            duration: 0.15,
                            ease: [0.25, 1, 0.5, 1],
                        }),
                        animate(y, y.get() + newY, {
                            type: "tween",
                            duration: 0.15,
                            ease: [0.25, 1, 0.5, 1],
                        })
                    ];

                    await Promise.all(promises);
                }
            }
        }

        document.addEventListener("mouseleave", handleMouseLeave);
        document.addEventListener("focus", handleFocus, true);

        return () => {
            document.removeEventListener("mouseleave", handleMouseLeave);
            document.removeEventListener("focus", handleFocus, true);
        }
    }, [x, y, isTouchDevice, containerRef])

    useEffect(() => {
        const initilize = () => {
            const containerBounds = containerRef.current.getBoundingClientRect();

            if (!initialBoundsRef.current || initialBoundsRef.current.index !== currentScreen || initialBoundsRef.current.isFullScreen !== isFullScreen) {
                boxRef.current.style.width = "fit-content";
                boxRef.current.style.height = "fit-content";
                const boxBounds = boxRef.current.getBoundingClientRect();

                initialBoundsRef.current = {
                    width: boxBounds.width,
                    height: boxBounds.height,
                    index: currentScreen,
                    isFullScreen
                }
            }

            const bankHeight = document.documentElement.clientHeight * 0.12;

            const originalWidth = initialBoundsRef.current.width;
            const originalHeight = initialBoundsRef.current.height;

            const containerHeight = containerBounds.height - bankHeight;
            const containerWidth = containerBounds.width;

            const widthRatio = containerWidth / originalWidth;
            const heightRatio = containerHeight / originalHeight;

            const ratio = Math.min(widthRatio, heightRatio);
            setChildrenScale(ratio);

            const scaledWidth = originalWidth * ratio;
            const scaledHeight = originalHeight * ratio;

            const positionY = ((containerHeight - scaledHeight) / 2) + bankHeight;
            const positionX = (containerWidth - scaledWidth) / 2;

            scale.set(1)
            y.set(positionY);
            x.set(positionX);

            setBounds({
                width: scaledWidth,
                height: scaledHeight,
                x: positionX,
                y: (positionY)
            });
        }

        window.addEventListener("resize", initilize);

        setTimeout(() => {
            // wait for the cells to be rendered
            initilize();
        }, 100);

        return () => {
            window.removeEventListener("resize", initilize);
        };
    }, [scale, x, y, currentScreen, isFullScreen, containerRef]);

    const centerBox = () => {
        const animations = [
            animate(x, bounds.x, {
                type: "tween",
                duration: 0.15,
                ease: [0.25, 1, 0.5, 1],
            }),
            animate(y, bounds.y, {
                type: "tween",
                duration: 0.15,
                ease: [0.25, 1, 0.5, 1],
            })
        ];

        return Promise.all(animations)
    }

    useGesture({
        onDrag: ({ offset: [dx, dy], pinching, event, distance }) => {
            if (pinching) return;

            if (distance.every(d => d === 0)) return;

            setCursor("grabbing");
            x.set(dx);
            y.set(dy);

            event.preventDefault();
            event.stopImmediatePropagation();
        },
        onDragEnd: ({ event }) => {
            setCursor("grab");
            event.preventDefault();
            event.stopPropagation();
        },
        onMouseMove: ({ dragging, event }) => {
            if (dragging) return;

            interactiveRef.current.style.pointerEvents = "none";
            const element = document.elementFromPoint(event.clientX, event.clientY);
            const cursorName = getCursor(element?.tagName);
            setCursor(cursorName);


            interactiveRef.current.style.pointerEvents = "all";
        },
        onPinch: ({ origin: [ox, oy], first, movement: [ms], offset: [s], memo }) => {
            if (first) {
                const bounds = interactiveRef.current.getBoundingClientRect();
                const tx = ox - (bounds.x + bounds.width / 2)
                const ty = oy - (bounds.y + bounds.height / 2)

                memo = { tx, ty, x: x.get(), y: y.get() }
            }

            const { tx, ty, x: initialX, y: initialY } = memo;

            const newX = initialX - (ms - 1) * tx;
            const newY = initialY - (ms - 1) * ty;

            const point = calculateAdjustedBounds({ x: newX, y: newY });
            y.set(point.y);
            x.set(point.x);
            scale.set(s);

            return memo;
        },
        onWheel: ({ offset: [, oy], movement: [, my], first, memo, event }) => {
            event.preventDefault();
            if (first) {
                const image = interactiveRef.current.getBoundingClientRect();

                const zoom = scale.get();
                const tx = (event.pageX - (image.x + image.width / 2)) / zoom;
                const ty = (event.pageY - (image.y + image.height / 2)) / zoom;
                const isNotchedWheel = Math.abs(event.wheelDelta ?? 0) > 100;

                const dScale = (-1 * oy) / RatioWheel;

                memo = { tx, ty, initialX: x.get(), initialY: y.get(), isNotchedWheel };

                if (zoom === 1 && dScale === 1) {
                    scale.set(.98)
                    animate(scale, 1, {
                        type: "spring",
                        stiffness: 100,
                        duration: 0.2,
                    });
                    return memo;
                }
            }

            const dScale = (-1 * oy) / RatioWheel;
            const mScale = (-1 * my) / RatioWheel;

            const { tx, ty, initialX, initialY, isNotchedWheel } = memo;
            const dx = initialX - mScale * tx;
            const dy = initialY - mScale * ty;

            if (isNotchedWheel) {
                y.stop();
                x.stop();
                scale.stop();
            }
            const duration = Math.abs(dScale - scale.get()) * 0.5;

            if (dScale === 1) {
                animate(x, bounds.x, {
                    type: "tween",
                    duration: 0.15,
                    ease: [0.25, 1, 0.5, 1],
                });
                animate(y, bounds.y, {
                    type: "tween",
                    duration: 0.15,
                    ease: [0.25, 1, 0.5, 1],
                });
            } else {
                if (isNotchedWheel) {
                    animate(x, dx, {
                        type: "tween",
                        duration,
                        ease: [0.25, 1, 0.5, 1],
                    });

                    animate(y, dy, {
                        type: "tween",
                        duration,
                        ease: [0.25, 1, 0.5, 1],
                    });
                } else {
                    x.set(dx);
                    y.set(dy);
                }
            }

            if (isNotchedWheel) {
                animate(scale, dScale, {
                    type: "tween",
                    duration,
                    ease: [0.25, 1, 0.5, 1],
                });
            } else {
                scale.set(dScale);
            }
            return memo;
        },
    }, {
        target: containerRef,
        eventOptions: { passive: false },
        pinch: {
            scaleBounds: {
                min: minScale,
                max: maxScale
            },
            rubberband: false,
            from: () => [scale.get(), 0],
            pinchOnWheel: false,

        },
        wheel: {
            from: () => [0, -scale.get() * RatioWheel],
            axis: "y",
            bounds: () => {
                return {
                    top: -maxScale * RatioWheel,
                    bottom: -minScale * RatioWheel
                }
            }
        },
        drag: {
            from: () => [x.get(), y.get()],
            rubberband: true,
            bounds: getBounds,
            filterTaps: true,
            keyboardDisplacement: false,
            tapsThreshold: 6 // default is 3px
        }
    });

    const handleReset = () => {
        gameSound.play(CRUCIGRAMA_SOUNDS.MOVE);
        if (scale.get() === 1) {
            scale.set(.98)
            animate(scale, 1, {
                type: "spring",
                stiffness: 100,
                duration: 0.2,
            });
        } else {
            animate(scale, 1, {
                type: "tween",
                duration: 0.2,
                ease: [0.25, 1, 0.5, 1],
            });
        }

        centerBox();
    }

    const handleZoom = async (value) => {
        if (zooming) return;

        setZooming(true);
        const zoom = scale.get();
        const newZoom = zoom + value;
        if (newZoom <= minScale) {
            if (zoom === 1) {
                scale.set(.98)
            }

            const animations = [
                animate(scale, minScale, {
                    type: "spring",
                    stiffness: 100,
                    duration: 0.2,
                }),
                centerBox()
            ];

            await Promise.all(animations);

            setZooming(false);
            return;
        }

        if (newZoom >= maxScale) {
            if (zoom === maxScale) {
                scale.set(maxScale + .02)
            }

            await animate(scale, maxScale, {
                type: "spring",
                stiffness: 100,
                duration: 0.2,
            })

            setZooming(false);
            return;
        }

        await animate(scale, newZoom, {
            type: "tween",
            duration: 0.4,
            ease: [0.25, 1, 0.5, 1],
        });

        const point = calculateAdjustedBounds({
            x: x.get(),
            y: y.get(),
        });

        x.set(point.x);
        y.set(point.y);

        setZooming(false);
    }

    return (
        <div ref={containerRef} className={clsx(classes.wrapper)} style={{ cursor }}>
            <motion.div style={{ x, y, scale, height: bounds.height, width: bounds.width }} ref={boxRef}>
                <div ref={interactiveRef} className={classes.interactiveArea} />

                <div style={{ transform: `scale(${childrenScale})`, transformOrigin: "0 0" }} id="scale-child">
                    {children}
                </div>

            </motion.div>


            <footer className={clsx(isTouchDevice && classes.footerdown, classes.footer)}>
                {!isTouchDevice &&
                    (
                        <>
                            <motion.button data-animate-id="nav-button" initial={{x: 100}} type="button" className={clsx(isTouchDevice && classes.dontShow, classes.zoomInButton)} onClick={() => handleZoom(.75)}>
                                <ZoomInIcon />
                            </motion.button>
                            <motion.button data-animate-id="nav-button"  initial={{x: 100}} type="button" className={clsx(isTouchDevice && classes.dontShow, classes.zoomOutButton)} onClick={() => handleZoom(-.75)}>
                                <ZoomOutIcon />
                            </motion.button>
                        </>
                    )}
                <motion.button data-animate-id="nav-button"  initial={{x: 100}} type="button" className={clsx(scale.current > 1 && classes.centerButtonactived, classes.centerButton)} onClick={handleReset}>
                    <CenterIcon />
                </motion.button>
            </footer>
        </div>

    )
})

InteractiveBox.propTypes = {
    children: PropTypes.node
};